'use client';

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type React from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MDEditor from '@uiw/react-md-editor';
import { getCommands, getExtraCommands } from '@uiw/react-md-editor/commands-cn';
import type { ICommand } from '@uiw/react-md-editor';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  FolderPlus,
  GripVertical,
  Image as ImageIcon,
  Loader2,
  Plus,
  Save,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';
import { apiFetch } from '@/lib/api/client';
import { uploadFile } from '@/lib/api/upload';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Z } from '@/lib/z-index';

type NodeKind = 'chapter' | 'step';

interface CourseDraft {
  title: string;
  intro: string;
  description: string;
  coverImage: string;
}

interface ChapterDraft {
  localId: string;
  serverId?: number;
  title: string;
  intro: string;
  prompt: string;
  objective: string;
  sortOrder: number;
  steps: StepDraft[];
}

interface StepDraft {
  localId: string;
  serverId?: number;
  title: string;
  content: string;
  sortOrder: number;
}

export interface CourseCreateEditData {
  course: {
    id: number;
    title: string;
    intro?: string;
    description?: string;
    coverImage?: string;
    sortOrder?: number;
    status?: number;
  };
  chapters?: Array<{
    id: number;
    courseId: number;
    title: string;
    intro?: string;
    objective?: string;
    description?: string;
    sortOrder?: number;
    status?: number;
  }>;
  steps?: Array<{
    id: number;
    chapterId: number;
    title: string;
    contentType?: string;
    content?: string;
    sortOrder?: number;
  }>;
}

type Selection =
  | { kind: 'chapter'; chapterId: string }
  | { kind: 'step'; chapterId: string; stepId: string };

interface AiChatMessage {
  role: 'system' | 'user' | 'ai';
  content: string;
}

const ADMIN = '/admin/api/v1/college';
const CHAPTER_DND = 'chapter:';
const STEP_DND = 'step:';

const makeId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const defaultMarkdown = `# 步骤目标

写下本步骤的学习内容。

## 操作说明

- 

## 检查点

- 
`;

function createEmptyChapter(index: number): ChapterDraft {
  return {
    localId: makeId('chapter'),
    title: `第 ${index} 章`,
    intro: '',
    prompt: '',
    objective: '',
    sortOrder: index,
    steps: [],
  };
}

function createEmptyStep(index: number): StepDraft {
  return {
    localId: makeId('step'),
    title: `步骤 ${index}`,
    content: defaultMarkdown,
    sortOrder: index,
  };
}

function chapterDndId(chapterId: string) {
  return `${CHAPTER_DND}${chapterId}`;
}

function stepDndId(chapterId: string, stepId: string) {
  return `${STEP_DND}${chapterId}:${stepId}`;
}

function parseDndId(id: string) {
  if (id.startsWith(CHAPTER_DND)) {
    return { kind: 'chapter' as const, chapterId: id.slice(CHAPTER_DND.length) };
  }
  if (id.startsWith(STEP_DND)) {
    const rest = id.slice(STEP_DND.length);
    const separator = rest.indexOf(':');
    if (separator < 0) return null;
    return {
      kind: 'step' as const,
      chapterId: rest.slice(0, separator),
      stepId: rest.slice(separator + 1),
    };
  }
  return null;
}

function findSelection(chapters: ChapterDraft[], selection: Selection) {
  const chapter = chapters.find((item) => item.localId === selection.chapterId) ?? null;
  if (!chapter || selection.kind === 'chapter') return { chapter, step: null };
  return {
    chapter,
    step: chapter.steps.find((item) => item.localId === selection.stepId) ?? null,
  };
}

function normalizeChapterOrder(chapters: ChapterDraft[]) {
  return chapters.map((chapter, index) => ({
    ...chapter,
    sortOrder: index + 1,
    steps: normalizeStepOrder(chapter.steps),
  }));
}

function normalizeStepOrder(steps: StepDraft[]) {
  return steps.map((step, index) => ({ ...step, sortOrder: index + 1 }));
}

async function createAdminCourse(data: {
  title: string;
  intro?: string;
  description?: string;
  coverImage?: string;
  sortOrder?: number;
  status?: number;
}): Promise<{ id: number }> {
  return apiFetch<{ id: number }>(`${ADMIN}/course`, {
    method: 'POST',
    body: data,
  });
}

async function updateAdminCourse(id: number, data: {
  title: string;
  intro?: string;
  description?: string;
  coverImage?: string;
  sortOrder?: number;
  status?: number;
}): Promise<void> {
  await apiFetch(`${ADMIN}/course/${id}`, {
    method: 'PUT',
    body: data,
  });
}

async function bindChaptersToCourse(courseId: number, data: { chapters: { chapterId: number; sortOrder: number }[] }): Promise<void> {
  await apiFetch(`${ADMIN}/course/${courseId}/chapters/bind`, {
    method: 'POST',
    body: data,
  });
}

async function createAdminChapter(data: {
  courseId: number;
  title: string;
  intro?: string;
  description?: string;
  objective?: string;
  sortOrder?: number;
  status?: number;
}): Promise<{ id: number }> {
  return apiFetch<{ id: number }>(`${ADMIN}/chapter`, {
    method: 'POST',
    body: data,
  });
}

async function updateAdminChapter(id: number, data: {
  courseId: number;
  title: string;
  intro?: string;
  description?: string;
  objective?: string;
  sortOrder?: number;
  status?: number;
}): Promise<void> {
  await apiFetch(`${ADMIN}/chapter/${id}`, {
    method: 'PUT',
    body: data,
  });
}

async function deleteAdminChapter(id: number): Promise<void> {
  await apiFetch(`${ADMIN}/chapter/${id}`, {
    method: 'DELETE',
  });
}

async function createAdminStep(data: {
  chapterId: number;
  title: string;
  contentType: 'md';
  content: string;
  sortOrder?: number;
}): Promise<{ id: number }> {
  return apiFetch<{ id: number }>(`${ADMIN}/step`, {
    method: 'POST',
    body: data,
  });
}

async function updateAdminStep(id: number, data: {
  chapterId: number;
  title: string;
  contentType: 'md';
  content: string;
  sortOrder?: number;
}): Promise<void> {
  await apiFetch(`${ADMIN}/step/${id}`, {
    method: 'PUT',
    body: data,
  });
}

async function deleteAdminStep(id: number): Promise<void> {
  await apiFetch(`${ADMIN}/step/${id}`, {
    method: 'DELETE',
  });
}

function preprocessAiChat(markdown: string): string {
  const aiChatRegex = /:::ai-chat\n([\s\S]*?)\n:::end/g;
  return markdown.replace(aiChatRegex, (_match, content) => {
    const messages: AiChatMessage[] = [];
    const lines = content
      .split('\n')
      .map((line: string) => line.trim())
      .filter(Boolean);
    let currentRole: AiChatMessage['role'] | null = null;

    for (const line of lines) {
      if (line.startsWith('[system]')) {
        currentRole = 'system';
        messages.push({ role: 'system', content: line.replace('[system]', '').trim() });
      } else if (line.startsWith('[user]')) {
        currentRole = 'user';
        messages.push({ role: 'user', content: line.replace('[user]', '').trim() });
      } else if (line.startsWith('[ai]')) {
        currentRole = 'ai';
        messages.push({ role: 'ai', content: line.replace('[ai]', '').trim() });
      } else if (currentRole) {
        const last = messages.at(-1);
        if (last) last.content = last.content ? `${last.content}\n${line}` : line;
      }
    }

    return `<ai-chat-block data-messages="${JSON.stringify(messages).replace(/"/g, '&quot;')}"></ai-chat-block>`;
  });
}

function AiChatPreviewBlock({ messages: messagesStr }: { messages: string }) {
  let messages: AiChatMessage[] = [];
  try {
    messages = JSON.parse(messagesStr);
  } catch {
    return <div className="p-4 text-sm text-destructive">解析 AI 对话失败</div>;
  }

  const systemMsg = messages.find((item) => item.role === 'system');
  const chatMessages = messages.filter((item) => item.role !== 'system');

  return (
    <div className="my-4 overflow-hidden rounded-xl border bg-card">
      <div className="border-b px-4 py-3 text-sm font-semibold text-muted-foreground">AI 对话</div>
      {systemMsg && <div className="border-b bg-amber-50 px-4 py-2.5 text-xs leading-relaxed text-amber-800">{systemMsg.content}</div>}
      <div className="space-y-3 px-4 py-3">
        {chatMessages.map((message, index) => (
          <div className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')} key={index}>
            <div
              className={cn(
                'max-w-[75%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
                message.role === 'user' ? 'rounded-br-sm bg-primary text-primary-foreground' : 'rounded-bl-sm bg-muted text-foreground',
              )}
            >
              {message.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function makeUploadCommand(type: 'image' | 'video', onUpload: (file: File) => Promise<void>): ICommand {
  const accept = type === 'image' ? 'image/*' : 'video/*';
  const label = type === 'image' ? '上传图片' : '上传视频';
  const icon =
    type === 'image'
      ? '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>'
      : '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>';

  return {
    name: `upload-${type}`,
    keyCommand: `upload-${type}`,
    buttonProps: { title: label, 'aria-label': label },
    icon: <span dangerouslySetInnerHTML={{ __html: icon }} />,
    execute: () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.onchange = () => {
        const file = input.files?.[0];
        if (file) void onUpload(file);
      };
      input.click();
    },
  };
}

const insertAiChatCommand: ICommand = {
  name: 'ai-chat',
  keyCommand: 'ai-chat',
  buttonProps: { title: '插入 AI 对话', 'aria-label': '插入 AI 对话' },
  icon: (
    <svg aria-label="AI对话" fill="none" height="12" role="img" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  execute: (_state, api) => {
    api.replaceSelection('\n:::ai-chat\n[system] 你是一位专业的 AI Office 助教\n[ai] 你好，我会陪你完成这个练习。\n:::end\n');
  },
};

export function CourseCreateModal({
  open,
  onClose,
  onSuccess,
  editCourse,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editCourse?: CourseCreateEditData | null;
}) {
  const toast = useToast();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [course, setCourse] = useState<CourseDraft>({ title: '', intro: '', description: '', coverImage: '' });
  const [chapters, setChapters] = useState<ChapterDraft[]>(() => [createEmptyChapter(1)]);
  const [selection, setSelection] = useState<Selection>(() => ({ kind: 'chapter', chapterId: chapters[0]?.localId ?? '' }));
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [renaming, setRenaming] = useState<{ kind: NodeKind; id: string } | null>(null);

  const [activeDndId, setActiveDndId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [wizardStep, setWizardStep] = useState<'content' | 'info'>('content');
  const [coverUploading, setCoverUploading] = useState(false);
  const isEditMode = !!editCourse;

  useEffect(() => {
    if (!open) return;
    if (editCourse) {
      const editChapters = (editCourse.chapters ?? [])
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((chapter, index) => ({
          localId: `chapter-${chapter.id}`,
          serverId: chapter.id,
          title: chapter.title || `第 ${index + 1} 章`,
          intro: chapter.intro || '',
          prompt: chapter.description || '',
          objective: chapter.objective || '',
          sortOrder: index + 1,
          steps: (editCourse.steps ?? [])
            .filter((step) => step.chapterId === chapter.id)
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((step, stepIndex) => ({
              localId: `step-${step.id}`,
              serverId: step.id,
              title: step.title || `步骤 ${stepIndex + 1}`,
              content: step.content || '',
              sortOrder: stepIndex + 1,
            })),
        }));
      const fallbackChapter = createEmptyChapter(1);
      const nextChapters = editChapters.length > 0 ? editChapters : [fallbackChapter];

      setCourse({
        title: editCourse.course.title || '',
        intro: editCourse.course.intro || '',
        description: editCourse.course.description || '',
        coverImage: editCourse.course.coverImage || '',
      });
      setChapters(nextChapters);
      setSelection({ kind: 'chapter', chapterId: nextChapters[0].localId });
      setExpandedIds(new Set(nextChapters.map((chapter) => chapter.localId)));
    } else {
      const firstChapter = createEmptyChapter(1);
      setCourse({ title: '', intro: '', description: '', coverImage: '' });
      setChapters([firstChapter]);
      setSelection({ kind: 'chapter', chapterId: firstChapter.localId });
      setExpandedIds(new Set([firstChapter.localId]));
    }
    setRenaming(null);
    setActiveDndId(null);
    setWizardStep('content');
  }, [editCourse, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const selected = useMemo(() => findSelection(chapters, selection), [chapters, selection]);
  const canProceedToInfo = chapters.length > 0 && chapters.every((chapter) => chapter.title.trim().length > 0) && !saving;
  const canPublish = course.title.trim().length > 0 && !saving;

  const updateChapter = useCallback((chapterId: string, patch: Partial<ChapterDraft>) => {
    setChapters((prev) => prev.map((chapter) => (chapter.localId === chapterId ? { ...chapter, ...patch } : chapter)));
  }, []);

  const updateStep = useCallback((chapterId: string, stepId: string, patch: Partial<StepDraft>) => {
    setChapters((prev) =>
      prev.map((chapter) =>
        chapter.localId === chapterId
          ? { ...chapter, steps: chapter.steps.map((step) => (step.localId === stepId ? { ...step, ...patch } : step)) }
          : chapter,
      ),
    );
  }, []);

  const addChapter = useCallback(() => {
    const chapter = createEmptyChapter(chapters.length + 1);
    setChapters((prev) => normalizeChapterOrder([...prev, chapter]));
    setExpandedIds((prev) => new Set(prev).add(chapter.localId));
    setSelection({ kind: 'chapter', chapterId: chapter.localId });
    setRenaming({ kind: 'chapter', id: chapter.localId });
  }, [chapters.length]);

  const addStep = useCallback(() => {
    const chapterId = selection.chapterId || chapters[0]?.localId;
    const chapter = chapters.find((item) => item.localId === chapterId);
    if (!chapter) return;
    const step = createEmptyStep(chapter.steps.length + 1);
    setChapters((prev) =>
      prev.map((item) => (item.localId === chapterId ? { ...item, steps: normalizeStepOrder([...item.steps, step]) } : item)),
    );
    setExpandedIds((prev) => new Set(prev).add(chapterId));
    setSelection({ kind: 'step', chapterId, stepId: step.localId });
    setRenaming({ kind: 'step', id: step.localId });
  }, [chapters, selection.chapterId]);

  const deleteSelection = useCallback(
    (target: Selection) => {
      if (target.kind === 'chapter') {
        if (chapters.length <= 1) {
          toast.info('至少保留一个章节');
          return;
        }
        const index = chapters.findIndex((chapter) => chapter.localId === target.chapterId);
        const next = normalizeChapterOrder(chapters.filter((chapter) => chapter.localId !== target.chapterId));
        setChapters(next);
        const fallback = next[Math.max(0, index - 1)] ?? next[0];
        setSelection({ kind: 'chapter', chapterId: fallback.localId });
        return;
      }

      setChapters((prev) =>
        prev.map((chapter) =>
          chapter.localId === target.chapterId
            ? { ...chapter, steps: normalizeStepOrder(chapter.steps.filter((step) => step.localId !== target.stepId)) }
            : chapter,
        ),
      );
      setSelection({ kind: 'chapter', chapterId: target.chapterId });
    },
    [chapters, toast],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDndId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDndId(null);
    const active = parseDndId(String(event.active.id));
    const over = event.over ? parseDndId(String(event.over.id)) : null;
    if (!active || !over || String(event.active.id) === String(event.over?.id)) return;

    if (active.kind === 'chapter' && over.kind === 'chapter') {
      setChapters((prev) => {
        const oldIndex = prev.findIndex((chapter) => chapter.localId === active.chapterId);
        const newIndex = prev.findIndex((chapter) => chapter.localId === over.chapterId);
        if (oldIndex < 0 || newIndex < 0) return prev;
        return normalizeChapterOrder(arrayMove(prev, oldIndex, newIndex));
      });
      return;
    }

    if (active.kind === 'step' && over.kind === 'step' && active.chapterId === over.chapterId) {
      setChapters((prev) =>
        prev.map((chapter) => {
          if (chapter.localId !== active.chapterId) return chapter;
          const oldIndex = chapter.steps.findIndex((step) => step.localId === active.stepId);
          const newIndex = chapter.steps.findIndex((step) => step.localId === over.stepId);
          if (oldIndex < 0 || newIndex < 0) return chapter;
          return { ...chapter, steps: normalizeStepOrder(arrayMove(chapter.steps, oldIndex, newIndex)) };
        }),
      );
    }
  }, []);



  const handleNextStep = useCallback(() => {
    for (const [index, chapter] of chapters.entries()) {
      if (!chapter.title.trim()) {
        toast.info(`第 ${index + 1} 个章节标题不能为空`);
        setSelection({ kind: 'chapter', chapterId: chapter.localId });
        return;
      }
    }
    setWizardStep('info');
  }, [chapters, toast]);

  const handleCoverUpload = useCallback(async (file: File) => {
    setCoverUploading(true);
    try {
      const url = await uploadFile(file);
      setCourse((prev) => ({ ...prev, coverImage: url }));
    } catch (error) {
      console.error('封面上传失败', error);
      toast.error('封面上传失败');
    } finally {
      setCoverUploading(false);
    }
  }, [toast]);

  const handleSave = useCallback(async () => {
    if (!course.title.trim()) {
      toast.info('请填写课程标题');
      return;
    }

    setSaving(true);
    try {
      if (editCourse) {
        await updateAdminCourse(editCourse.course.id, {
          title: course.title.trim(),
          intro: course.intro.trim() || undefined,
          description: course.description.trim() || undefined,
          coverImage: course.coverImage.trim() || undefined,
          sortOrder: editCourse.course.sortOrder ?? 0,
          status: editCourse.course.status ?? 1,
        });

        const originalChapterIds = new Set((editCourse.chapters ?? []).map((chapter) => chapter.id));
        const currentChapterIds = new Set(chapters.map((chapter) => chapter.serverId).filter((id): id is number => typeof id === 'number'));
        const originalStepIds = new Set((editCourse.steps ?? []).map((step) => step.id));
        const currentStepIds = new Set(
          chapters
            .flatMap((chapter) => chapter.steps)
            .map((step) => step.serverId)
            .filter((id): id is number => typeof id === 'number'),
        );

        for (const stepId of originalStepIds) {
          if (!currentStepIds.has(stepId)) await deleteAdminStep(stepId);
        }
        for (const chapterId of originalChapterIds) {
          if (!currentChapterIds.has(chapterId)) await deleteAdminChapter(chapterId);
        }

        const boundChapters: { chapterId: number; sortOrder: number }[] = [];

        for (const [chapterIndex, chapter] of chapters.entries()) {
          const chapterPayload = {
            courseId: editCourse.course.id,
            title: chapter.title.trim(),
            intro: chapter.intro.trim() || undefined,
            description: chapter.prompt.trim() || undefined,
            objective: chapter.objective.trim() || undefined,
            sortOrder: chapterIndex + 1,
            status: 1,
          };
          let chapterId = chapter.serverId;
          if (chapterId) {
            await updateAdminChapter(chapterId, chapterPayload);
          } else {
            const createdChapter = await createAdminChapter(chapterPayload);
            chapterId = createdChapter.id;
          }

          boundChapters.push({ chapterId, sortOrder: chapterIndex + 1 });

          for (const [stepIndex, step] of chapter.steps.entries()) {
            if (!step.title.trim() && !step.content.trim()) continue;
            const stepPayload = {
              chapterId,
              title: step.title.trim() || `步骤 ${stepIndex + 1}`,
              contentType: 'md' as const,
              content: step.content,
              sortOrder: stepIndex + 1,
            };
            if (step.serverId) {
              await updateAdminStep(step.serverId, stepPayload);
            } else {
              await createAdminStep(stepPayload);
            }
          }
        }

        if (boundChapters.length > 0) {
          await bindChaptersToCourse(editCourse.course.id, { chapters: boundChapters });
        }

        toast.success('课程更新成功');
        window.dispatchEvent(new CustomEvent('academy:course-changed'));
        onSuccess?.();
        onClose();
        return;
      }

      const createdCourse = await createAdminCourse({
        title: course.title.trim(),
        intro: course.intro.trim() || undefined,
        description: course.description.trim() || undefined,
        coverImage: course.coverImage.trim() || undefined,
        sortOrder: 0,
        status: 1,
      });

      const createdChapterIds: { chapterId: number; sortOrder: number }[] = [];

      for (const [chapterIndex, chapter] of chapters.entries()) {
        const createdChapter = await createAdminChapter({
          courseId: createdCourse.id,
          title: chapter.title.trim(),
          intro: chapter.intro.trim() || undefined,
          description: chapter.prompt.trim() || undefined,
          objective: chapter.objective.trim() || undefined,
          sortOrder: chapterIndex + 1,
          status: 1,
        });

        createdChapterIds.push({ chapterId: createdChapter.id, sortOrder: chapterIndex + 1 });

        for (const [stepIndex, step] of chapter.steps.entries()) {
          if (!step.title.trim() && !step.content.trim()) continue;
          await createAdminStep({
            chapterId: createdChapter.id,
            title: step.title.trim() || `步骤 ${stepIndex + 1}`,
            contentType: 'md',
            content: step.content,
            sortOrder: stepIndex + 1,
          });
        }
      }

      if (createdChapterIds.length > 0) {
        await bindChaptersToCourse(createdCourse.id, { chapters: createdChapterIds });
      }

      toast.success('课程创建成功');
      window.dispatchEvent(new CustomEvent('academy:course-changed'));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(editCourse ? '课程更新失败，请稍后重试' : '课程创建失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  }, [chapters, course, editCourse, onClose, onSuccess, toast]);

  if (!open) return null;

  const chapterDndIds = chapters.map((chapter) => chapterDndId(chapter.localId));

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 bg-black/35 backdrop-blur-sm" onClick={onClose} style={{ zIndex: Z.MODAL_OVERLAY }} />
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT, pointerEvents: 'none' }}>
        <div className="flex h-[min(800px,92vh)] w-[min(1220px,96vw)] flex-col overflow-hidden rounded-2xl border border-border/40 bg-background shadow-2xl" style={{ pointerEvents: 'auto' }}>
          <header className="flex shrink-0 items-center justify-between border-b border-border/40 px-5 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <BookOpen className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-foreground">{isEditMode ? '编辑课程' : '创建课程'}</h2>
                <div className="mt-1 flex items-center gap-1.5">
                  {([
                    { key: 'content', label: '编辑内容' },
                    { key: 'info', label: '课程信息' },
                  ] as const).map((step, i) => (
                    <Fragment key={step.key}>
                      {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/50" />}
                      <button
                        className={cn(
                          'rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors',
                          wizardStep === step.key
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                        onClick={() => {
                          if (step.key === 'content' && wizardStep === 'info') setWizardStep('content');
                        }}
                        type="button"
                        disabled={step.key === 'info' && wizardStep === 'content' && !canProceedToInfo}
                      >
                        {step.label}
                      </button>
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
            <button className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground" onClick={onClose} type="button">
              <X className="h-4 w-4" />
            </button>
          </header>

          {wizardStep === 'content' ? (
            <div className="grid min-h-0 flex-1 grid-cols-[340px_minmax(0,1fr)]">
              <aside className="flex min-h-0 flex-col border-r border-border/40 bg-secondary/20">
                <div className="space-y-3 border-b border-border/40 p-4">
                  <div className="flex items-center gap-2">
                    <button className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90" onClick={addChapter} type="button">
                      <FolderPlus className="h-3.5 w-3.5" />
                      新增章节
                    </button>
                    <button className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-border/60 bg-background text-xs font-medium text-foreground transition-colors hover:bg-secondary" onClick={addStep} type="button">
                      <Plus className="h-3.5 w-3.5" />
                      新增步骤
                    </button>
                  </div>
                </div>

                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} onDragStart={handleDragStart} sensors={sensors}>
                  <div className="flex-1 overflow-y-auto p-3">
                    <SortableContext items={chapterDndIds} strategy={verticalListSortingStrategy}>
                      <div className="space-y-1">
                        {chapters.map((chapter, chapterIndex) => {
                          const expanded = expandedIds.has(chapter.localId);
                          return (
                            <div key={chapter.localId}>
                              <SortableTreeRow
                                active={selection.kind === 'chapter' && selection.chapterId === chapter.localId}
                                dndId={chapterDndId(chapter.localId)}
                                expandIcon={expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                indent={0}
                                label={chapter.title}
                                muted={activeDndId === chapterDndId(chapter.localId)}
                                nodeIcon={<BookOpen className="h-3.5 w-3.5" />}
                                onDelete={() => deleteSelection({ kind: 'chapter', chapterId: chapter.localId })}
                                onDoubleClick={() => setRenaming({ kind: 'chapter', id: chapter.localId })}
                                onExpand={() => {
                                  setExpandedIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(chapter.localId)) next.delete(chapter.localId);
                                    else next.add(chapter.localId);
                                    return next;
                                  });
                                }}
                                onRename={(value) => updateChapter(chapter.localId, { title: value || `第 ${chapterIndex + 1} 章` })}
                                onSelect={() => setSelection({ kind: 'chapter', chapterId: chapter.localId })}
                                renaming={renaming?.kind === 'chapter' && renaming.id === chapter.localId}
                                setRenaming={setRenaming}
                              />
                              {expanded && (
                                <SortableContext items={chapter.steps.map((step) => stepDndId(chapter.localId, step.localId))} strategy={verticalListSortingStrategy}>
                                  <div className="mt-1 space-y-1">
                                    {chapter.steps.map((step, stepIndex) => (
                                      <SortableTreeRow
                                        active={selection.kind === 'step' && selection.stepId === step.localId}
                                        dndId={stepDndId(chapter.localId, step.localId)}
                                        indent={1}
                                        key={step.localId}
                                        label={step.title}
                                        muted={activeDndId === stepDndId(chapter.localId, step.localId)}
                                        nodeIcon={<FileText className="h-3.5 w-3.5" />}
                                        onDelete={() => deleteSelection({ kind: 'step', chapterId: chapter.localId, stepId: step.localId })}
                                        onDoubleClick={() => setRenaming({ kind: 'step', id: step.localId })}
                                        onRename={(value) => updateStep(chapter.localId, step.localId, { title: value || `步骤 ${stepIndex + 1}` })}
                                        onSelect={() => setSelection({ kind: 'step', chapterId: chapter.localId, stepId: step.localId })}
                                        renaming={renaming?.kind === 'step' && renaming.id === step.localId}
                                        setRenaming={setRenaming}
                                      />
                                    ))}
                                    {chapter.steps.length === 0 && (
                                      <button className="ml-8 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-background hover:text-foreground" onClick={addStep} type="button">
                                        添加第一个步骤
                                      </button>
                                    )}
                                  </div>
                                </SortableContext>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </SortableContext>
                  </div>
                </DndContext>
              </aside>

              <main className="flex min-h-0 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto p-5">
                  {selected.step && selected.chapter ? (
                    <StepEditor chapterTitle={selected.chapter.title} step={selected.step} update={(patch) => updateStep(selected.chapter!.localId, selected.step!.localId, patch)} />
                  ) : selected.chapter ? (
                    <ChapterEditor chapter={selected.chapter} update={(patch) => updateChapter(selected.chapter!.localId, patch)} />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">请选择章节或步骤</div>
                  )}
                </div>

                <footer className="flex shrink-0 items-center justify-between border-t border-border/40 bg-secondary/20 px-5 py-3">
                  <span className="text-xs text-muted-foreground">
                    {chapters.length} 个章节，{chapters.reduce((sum, chapter) => sum + chapter.steps.length, 0)} 个步骤
                  </span>
                  <button
                    className="flex h-9 min-w-[108px] items-center justify-center gap-2 rounded-lg bg-primary px-4 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!canProceedToInfo}
                    onClick={() => handleNextStep()}
                    type="button"
                  >
                    下一步
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </footer>
              </main>
            </div>
          ) : (
            <CourseInfoStep
              course={course}
              onChange={setCourse}
              coverUploading={coverUploading}
              onCoverUpload={handleCoverUpload}
              onBack={() => setWizardStep('content')}
              onSubmit={() => void handleSave()}
              saving={saving}
              canPublish={canPublish}
              chapters={chapters}
              isEditMode={isEditMode}
            />
          )}
        </div>
      </div>

    </>,
    document.body,
  );
}

function SortableTreeRow({
  active,
  dndId,
  expandIcon,
  indent,
  label,
  muted,
  nodeIcon,
  onDelete,
  onDoubleClick,
  onExpand,
  onRename,
  onSelect,
  renaming,
  setRenaming,
}: {
  active: boolean;
  dndId: string;
  expandIcon?: React.ReactNode;
  indent: number;
  label: string;
  muted: boolean;
  nodeIcon: React.ReactNode;
  onDelete: () => void;
  onDoubleClick: () => void;
  onExpand?: () => void;
  onRename: (value: string) => void;
  onSelect: () => void;
  renaming: boolean;
  setRenaming: (value: { kind: NodeKind; id: string } | null) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dndId });
  const [value, setValue] = useState(label);

  useEffect(() => {
    setValue(label);
  }, [label, renaming]);

  const commit = () => {
    onRename(value.trim());
    setRenaming(null);
  };

  return (
    <div
      className={cn(
        'group flex h-8 cursor-pointer items-center gap-1.5 rounded-lg pr-2 text-xs transition-colors',
        active ? 'bg-primary/10 text-primary' : 'text-foreground/80 hover:bg-background',
        (isDragging || muted) && 'opacity-40',
      )}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
      ref={setNodeRef}
      style={{
        paddingLeft: 8 + indent * 18,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <button
        {...attributes}
        {...listeners}
        aria-label="拖拽排序"
        className="flex h-5 w-5 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground/50 hover:bg-secondary hover:text-muted-foreground active:cursor-grabbing"
        onClick={(event) => event.stopPropagation()}
        type="button"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      {expandIcon ? (
        <button
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
          onClick={(event) => {
            event.stopPropagation();
            onExpand?.();
          }}
          type="button"
        >
          {expandIcon}
        </button>
      ) : (
        <span className="h-5 w-5 shrink-0" />
      )}
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">{nodeIcon}</span>
      {renaming ? (
        <input
          autoFocus
          className="h-6 min-w-0 flex-1 rounded-md border border-primary/40 bg-background px-2 text-xs text-foreground outline-none"
          onBlur={commit}
          onChange={(event) => setValue(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
            if (event.key === 'Escape') setRenaming(null);
          }}
          value={value}
        />
      ) : (
        <span className="min-w-0 flex-1 truncate">{label}</span>
      )}
      <button
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/0 group-hover:text-muted-foreground/60 hover:!text-red-500 hover:bg-red-500/10 transition-colors"
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
        title="删除"
        type="button"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="text-xs font-medium text-muted-foreground">{children}</label>;
}

function ChapterEditor({ chapter, update }: { chapter: ChapterDraft; update: (patch: Partial<ChapterDraft>) => void }) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">

      <section className="space-y-4 rounded-xl border border-border/40 bg-secondary/15 p-4">
        <div className="space-y-1.5">
          <FieldLabel>章节标题 <span className="text-destructive">*</span></FieldLabel>
          <input
            className="h-10 w-full rounded-xl border border-border/50 bg-background px-3 text-sm outline-none transition-colors focus:border-primary/50"
            onChange={(event) => update({ title: event.target.value })}
            value={chapter.title}
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel>简介</FieldLabel>
          <textarea
            className="min-h-24 w-full resize-y rounded-xl border border-border/50 bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/50"
            onChange={(event) => update({ intro: event.target.value })}
            placeholder="简要说明本章节会完成什么"
            value={chapter.intro}
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel>AI Office 演练提示词</FieldLabel>
          <textarea
            className="min-h-32 w-full resize-y rounded-xl border border-border/50 bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/50"
            onChange={(event) => update({ prompt: event.target.value })}
            placeholder="填写后学习者可用它作为 AI Office 演练上下文"
            value={chapter.prompt}
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel>学习目标</FieldLabel>
          <textarea
            className="min-h-28 w-full resize-y rounded-xl border border-border/50 bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/50"
            onChange={(event) => update({ objective: event.target.value })}
            placeholder="列出学习者完成本章后应掌握的能力"
            value={chapter.objective}
          />
        </div>
      </section>
    </div>
  );
}

function StepEditor({
  chapterTitle,
  step,
  update,
}: {
  chapterTitle: string;
  step: StepDraft;
  update: (patch: Partial<StepDraft>) => void;
}) {
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);

  const handleUpload = useCallback(
    async (file: File, type: 'image' | 'video') => {
      setUploadingFile(file.name);
      try {
        const url = await uploadFile(file);
        const markdown = type === 'image' ? `![图片](${url})` : `[视频 ${file.name}](${url})`;
        const textarea = document.querySelector('.course-create-md-editor .w-md-editor-text-input') as HTMLTextAreaElement | null;
        if (textarea) {
          const start = textarea.selectionStart ?? step.content.length;
          const end = textarea.selectionEnd ?? start;
          update({ content: step.content.slice(0, start) + markdown + step.content.slice(end) });
        } else {
          update({ content: `${step.content}\n${markdown}` });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setUploadingFile(null);
      }
    },
    [step.content, update],
  );

  const uploadImageCmd = useRef<ICommand | null>(null);
  const uploadVideoCmd = useRef<ICommand | null>(null);
  if (!uploadImageCmd.current) uploadImageCmd.current = makeUploadCommand('image', (file) => handleUpload(file, 'image'));
  if (!uploadVideoCmd.current) uploadVideoCmd.current = makeUploadCommand('video', (file) => handleUpload(file, 'video'));

  const handleFileEvent = (file: File) => {
    const type = file.type.startsWith('video/') ? 'video' : 'image';
    void handleUpload(file, type);
  };

  return (
    <div className="flex h-full min-h-[560px] flex-col gap-4">
      <div className="flex shrink-0 items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-medium text-primary">{chapterTitle}</p>
          <input
            className="mt-1 h-10 w-full min-w-[320px] rounded-xl border border-border/50 bg-background px-3 text-lg font-semibold text-foreground outline-none transition-colors focus:border-primary/50"
            onChange={(event) => update({ title: event.target.value })}
            value={step.title}
          />
        </div>
        <span className="shrink-0 rounded-full bg-secondary px-3 py-1 text-xs text-muted-foreground">Markdown</span>
      </div>
      <div
        className="course-create-md-editor relative min-h-0 flex-1 overflow-hidden rounded-xl border border-border/40 bg-background"
        data-color-mode="light"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const file = event.dataTransfer.files[0];
          if (file) handleFileEvent(file);
        }}
        onPaste={(event) => {
          const item = Array.from(event.clipboardData.items).find((candidate) => candidate.kind === 'file' && (candidate.type.startsWith('image/') || candidate.type.startsWith('video/')));
          const file = item?.getAsFile();
          if (!file) return;
          event.preventDefault();
          handleFileEvent(file);
        }}
      >
        {uploadingFile && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-background/70 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>正在上传 {uploadingFile}...</span>
            </div>
          </div>
        )}
        <MDEditor
          commands={[
            ...getCommands(),
            { name: 'divider', keyCommand: 'divider' },
            uploadImageCmd.current!,
            uploadVideoCmd.current!,
            { name: 'divider', keyCommand: 'divider' },
            insertAiChatCommand,
          ]}
          components={{
            preview: (markdown) => {
              const processed = preprocessAiChat(markdown);
              return (
                <div className="wmde-markdown wmde-markdown-color">
                  <ReactMarkdown
                    components={
                      {
                        'ai-chat-block': (props: Record<string, unknown>) => {
                          const messagesStr = props['data-messages'] as string | undefined;
                          return messagesStr ? <AiChatPreviewBlock messages={messagesStr} /> : null;
                        },
                      } as Record<string, React.ComponentType<Record<string, unknown>>>
                    }
                    rehypePlugins={[rehypeRaw]}
                  >
                    {processed}
                  </ReactMarkdown>
                </div>
              );
            },
          }}
          extraCommands={getExtraCommands()}
          height="100%"
          onChange={(value) => update({ content: value ?? '' })}
          textareaProps={{ 'aria-label': '步骤 Markdown 内容', placeholder: '请输入 Markdown 内容，支持拖拽或粘贴图片/视频' }}
          value={step.content}
          visibleDragbar={false}
        />
      </div>
    </div>
  );
}

function CourseInfoStep({
  course,
  onChange,
  onBack,
  onSubmit,
  onCoverUpload,
  coverUploading,
  saving,
  canPublish,
  chapters,
  isEditMode,
}: {
  course: CourseDraft;
  onChange: (form: CourseDraft) => void;
  onBack: () => void;
  onSubmit: () => void;
  onCoverUpload: (file: File) => void;
  coverUploading: boolean;
  saving: boolean;
  canPublish: boolean;
  chapters: ChapterDraft[];
  isEditMode?: boolean;
}) {
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [coverPreview, setCoverPreview] = useState(false);

  const set = <K extends keyof CourseDraft>(key: K, value: CourseDraft[K]) => onChange({ ...course, [key]: value });

  useEffect(() => {
    if (!coverPreview) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCoverPreview(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [coverPreview]);

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-2xl flex-col gap-6 p-6">
          {/* 左右分栏：封面 + 表单 */}
          <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-6">
            {/* 封面区域 */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">封面图片</label>
              {course.coverImage ? (
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl border bg-muted/20 group">
                  <img alt="封面预览" className="h-full w-full object-cover cursor-zoom-in" onClick={() => setCoverPreview(true)} src={course.coverImage} />
                  <button
                    className="absolute top-1.5 right-1.5 h-6 w-6 rounded-md bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                    onClick={() => set('coverImage', '')}
                    title="移除封面"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/60 bg-muted/20 text-muted-foreground transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={coverUploading}
                  onClick={() => coverInputRef.current?.click()}
                  type="button"
                >
                  {coverUploading ? (
                    <>
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="text-xs">上传中...</span>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="h-6 w-6" />
                      <span className="text-xs">上传封面</span>
                    </>
                  )}
                </button>
              )}
              <input
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onCoverUpload(file);
                  e.target.value = '';
                }}
                ref={coverInputRef}
                type="file"
              />
              <button
                className="flex h-7 w-full items-center justify-center gap-1 rounded-lg border border-border/60 bg-background text-[11px] font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground shrink-0"
                disabled={coverUploading}
                onClick={() => coverInputRef.current?.click()}
                type="button"
              >
                <ImageIcon className="h-3 w-3" />
                {course.coverImage ? '换一张' : '上传图片'}
              </button>
              <input
                className="h-7 w-full rounded-lg border border-border/50 bg-background px-2 text-[11px] outline-none transition-colors focus:border-primary/50"
                onChange={(e) => set('coverImage', e.target.value)}
                placeholder="或输入 URL"
                value={course.coverImage}
              />
            </div>

            {/* 表单区域 */}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">
                  课程标题 <span className="text-destructive">*</span>
                </label>
                <input
                  autoFocus
                  className="h-10 w-full rounded-xl border border-border/50 bg-background px-3 text-sm outline-none transition-colors focus:border-primary/50"
                  onChange={(e) => set('title', e.target.value)}
                  placeholder="请输入课程标题"
                  value={course.title}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">简介</label>
                <textarea
                  className="w-full rounded-xl border border-border/50 bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/50"
                  onChange={(e) => set('intro', e.target.value)}
                  placeholder="请输入课程简介"
                  rows={3}
                  value={course.intro}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">详细描述</label>
                <textarea
                  className="w-full rounded-xl border border-border/50 bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/50"
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="请输入课程描述"
                  rows={4}
                  value={course.description}
                />
              </div>
            </div>
          </div>

          {/* 课程结构概览 */}
          <section className="rounded-xl border border-border/40 bg-secondary/15 p-4">
            <h3 className="text-xs font-medium text-muted-foreground mb-3">课程结构概览</h3>
            <div className="space-y-1.5">
              {chapters.map((chapter, ci) => (
                <div key={chapter.localId} className="flex items-center gap-2 text-xs">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">{ci + 1}</span>
                  <span className="font-medium text-foreground truncate">{chapter.title}</span>
                  <span className="text-muted-foreground">{chapter.steps.length} 个步骤</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              共 {chapters.length} 个章节，{chapters.reduce((s, c) => s + c.steps.length, 0)} 个步骤
            </p>
          </section>
        </div>
      </div>

      {/* 全屏封面预览 */}
      {coverPreview && course.coverImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setCoverPreview(false)} style={{ zIndex: Z.MODAL_CONTENT + 1 }}>
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img alt="封面大图" className="max-w-[90vw] max-h-[85vh] rounded-xl object-contain shadow-2xl" src={course.coverImage} />
            <button className="absolute -top-3 -right-3 h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black transition-colors" onClick={() => setCoverPreview(false)} title="关闭预览" type="button">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <footer className="flex shrink-0 items-center justify-between border-t border-border/40 bg-secondary/20 px-5 py-3">
        <button
          className="flex h-9 items-center justify-center gap-2 rounded-lg border border-border/60 bg-background px-4 text-xs font-medium text-foreground transition-colors hover:bg-secondary"
          disabled={saving}
          onClick={onBack}
          type="button"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          上一步
        </button>
        <button
          className="flex h-9 min-w-[108px] items-center justify-center gap-2 rounded-lg bg-primary px-4 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canPublish || saving}
          onClick={onSubmit}
          type="button"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? (isEditMode ? '保存中' : '发布中') : (isEditMode ? '保存修改' : '发布课程')}
        </button>
      </footer>
    </>
  );
}
